import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export type ExportValue = string | number | boolean | null | undefined;
export interface ExportColumn<T> { label: string; value: (row: T) => ExportValue; }
export interface ExportOptions<T> { title: string; fileName: string; columns: ExportColumn<T>[]; rows: T[]; footer?: ExportValue[]; }

const normalizeValue = (value: ExportValue) => value == null ? '' : typeof value === 'boolean' ? (value ? 'Sim' : 'Não') : value;
const buildData = <T,>({ columns, rows }: ExportOptions<T>) => ({
  headers: columns.map((column) => column.label),
  body: rows.map((row) => columns.map((column) => normalizeValue(column.value(row)))),
});
const safeFileName = (fileName: string) => fileName.trim().replace(/[^a-zA-Z0-9-_]/g, '-').replace(/-+/g, '-');

const exportPdf = <T,>(options: ExportOptions<T>) => {
  const { headers, body } = buildData(options);
  const document = new jsPDF({ orientation: headers.length > 5 ? 'landscape' : 'portrait', unit: 'pt', format: 'a4' });
  document.setFontSize(14);
  document.text(options.title, 40, 34);
  autoTable(document, { head: [headers], body, foot: options.footer ? [options.footer.map(normalizeValue)] : undefined, startY: 48, styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' }, headStyles: { fillColor: [11, 18, 112] }, footStyles: { fillColor: [245, 245, 245], textColor: [11, 18, 112], fontStyle: 'bold' }, margin: { left: 24, right: 24 } });
  document.save(`${safeFileName(options.fileName)}.pdf`);
};

const exportXlsx = <T,>(options: ExportOptions<T>) => {
  const { headers, body } = buildData(options);
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body, ...(options.footer ? [options.footer.map(normalizeValue)] : [])]);
  worksheet['!cols'] = headers.map((header, index) => ({ wch: Math.min(50, Math.max(header.length + 2, ...body.map((row) => String(row[index]).length + 2))) }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Resultados');
  XLSX.writeFile(workbook, `${safeFileName(options.fileName)}.xlsx`);
};

const exportCsv = <T,>(options: ExportOptions<T>) => {
  const { headers, body } = buildData(options);
  const csv = XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet([headers, ...body, ...(options.footer ? [options.footer.map(normalizeValue)] : [])]), { FS: ';' });
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${safeFileName(options.fileName)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const ExportService = { exportPdf, exportXlsx, exportCsv };
